import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, CalendarClock, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState, PageLoading, fmtDate } from "@/components/shared";
import { DatePicker } from "@/components/DatePicker";
import api, { formatApiErrorDetail } from "@/lib/api";

const TYPES = ["Residence Permit Expiry", "Passport Expiry", "Employment Contract Expiry", "Document Renewal", "Custom"];
const SEVERITY = ["LOW", "MEDIUM", "HIGH"];

function DeadlineDialog({ employees, onDone }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", type: "Custom", title: "", description: "", due_date: "", severity: "MEDIUM" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.title || !f.due_date) { toast.error(t("dlp.req")); return; }
    setBusy(true);
    try {
      const payload = { ...f, employee_id: f.employee_id || null };
      await api.post("/employer/deadlines", payload);
      toast.success(t("dlp.created")); setOpen(false); setF({ employee_id: "", type: "Custom", title: "", description: "", due_date: "", severity: "MEDIUM" }); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="add-deadline-btn"><Plus className="mr-1 h-4 w-4" /> {t("dlp.add")}</Button></DialogTrigger>
      <DialogContent className="max-w-md" data-testid="deadline-dialog">
        <DialogHeader><DialogTitle>{t("dlp.addTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>{t("dlp.titleField")}</Label><Input value={f.title} onChange={(e) => set("title")(e.target.value)} data-testid="deadline-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{t("dlp.type")}</Label><Select value={f.type} onValueChange={set("type")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>{t("dlp.severity")}</Label><Select value={f.severity} onValueChange={set("severity")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SEVERITY.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="space-y-2"><Label>{t("dlp.employee")}</Label><Select value={f.employee_id} onValueChange={set("employee_id")}><SelectTrigger data-testid="deadline-employee"><SelectValue placeholder={t("dlp.none")} /></SelectTrigger><SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>{t("dlp.dueDate")}</Label><DatePicker value={f.due_date} onChange={set("due_date")} testid="deadline-date" /></div>
          <div className="space-y-2"><Label>{t("dlp.description")}</Label><Textarea rows={2} value={f.description} onChange={(e) => set("description")(e.target.value)} /></div>
        </div>
        <DialogFooter><Button disabled={busy} onClick={submit} className="rounded-full bg-navy text-white" data-testid="deadline-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("dlp.create")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeadlineRow({ d, onComplete }) {
  const { t } = useTranslation();
  const overdue = d.days_remaining != null && d.days_remaining < 0 && d.status !== "COMPLETED";
  const title = d.type === "RENEWAL" ? t("dlp.renewalTitle") : d.title;
  const typeLabel = d.type === "RENEWAL" ? t("dlp.renewalType") : d.type;
  return (
    <div className="flex items-center justify-between px-4 py-3" data-testid={`deadline-row`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${overdue ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-500"}`}><CalendarClock className="h-4 w-4" /></div>
        <div><div className="text-sm font-semibold text-navy">{title}</div><div className="text-xs text-slate-400">{d.employee_name || "—"} · {typeLabel}</div></div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right"><div className="text-sm text-navy">{fmtDate(d.due_date)}</div>
          {d.days_remaining != null && <div className={`text-xs ${overdue ? "text-red-500" : d.days_remaining <= 30 ? "text-amber-500" : "text-slate-400"}`}>{overdue ? t("dlp.daysOverdue", { n: Math.abs(d.days_remaining) }) : t("dlp.days", { n: d.days_remaining })}</div>}</div>
        {d.source === "manual" && d.status !== "COMPLETED" && <button onClick={() => onComplete(d)} className="rounded-lg p-2 text-slate-300 hover:bg-emerald-50 hover:text-emerald-600" data-testid="complete-deadline" title={t("dlp.markComplete")}><CheckCircle2 className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

export default function Deadlines() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const load = () => api.get("/employer/deadlines").then((r) => setItems(r.data.deadlines));
  useEffect(() => { load(); api.get("/employer/employees").then((r) => setEmployees(r.data.employees)); }, []);

  const complete = async (d) => { await api.put(`/employer/deadlines/${d.id}`, { status: "COMPLETED" }); toast.success(t("dlp.marked")); load(); };

  if (items === null) return <><PageHeader title={t("dlp.title")} /><PageLoading /></>;
  const upcoming = items.filter((d) => d.status !== "COMPLETED" && (d.days_remaining == null || d.days_remaining >= 0));
  const overdue = items.filter((d) => d.status !== "COMPLETED" && d.days_remaining != null && d.days_remaining < 0);
  const completed = items.filter((d) => d.status === "COMPLETED");

  const List = ({ arr, empty }) => arr.length === 0 ? <EmptyState icon={CalendarClock} title={t("dlp.caughtUp")} subtitle={empty} /> : <div className="card-soft divide-y divide-slate-50">{arr.map((d, i) => <DeadlineRow key={d.id + i} d={d} onComplete={complete} />)}</div>;

  return (
    <div data-testid="deadlines-page">
      <PageHeader title={t("dlp.title")} subtitle={t("dlp.subtitle")} action={<DeadlineDialog employees={employees} onDone={load} />} />
      <Tabs defaultValue="upcoming">
        <TabsList className="mb-6 bg-slate-100">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">{t("dlp.upcoming")} ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue">{t("dlp.overdue")} ({overdue.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">{t("dlp.completed")} ({completed.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming"><List arr={upcoming} empty={t("dlp.emptyUpcoming")} /></TabsContent>
        <TabsContent value="overdue"><List arr={overdue} empty={t("dlp.emptyOverdue")} /></TabsContent>
        <TabsContent value="completed"><List arr={completed} empty={t("dlp.emptyCompleted")} /></TabsContent>
      </Tabs>
    </div>
  );
}
